import React, { useMemo } from 'react';
import Applicants from './Applicants';
import Applications from './Applications';
import SubmitDashboard from './SubmitDashboard';

const SubmitApplication = ({ 
  user, 
  activeTab, 
  onTabChange, 
  onBack, 
  settings, 
  applicants = [],
  applications = [], 
  onSaveApplicant,
  onDeleteApplicant, 
  applicationRecords = [], 
  onSaveApplication,
  users = [] 
}) => {

  // 1. Identify if current user has global visibility permissions or is a Master Agent
  const currentUserData = useMemo(() => 
    (users || []).find(u => String(u.uid) === String(user?.uid)), 
    [users, user?.uid]
  );

  const hasGlobalView = useMemo(() => {
    return currentUserData?.role === 'Admin' || currentUserData?.role === 'TWG Apps and Visa';
  }, [currentUserData]);

  // Determine if the current view should be restricted to Read-Only
  const isAgentMaster = currentUserData?.role === 'TWG Agent Master';

  // 2. Filter applicants: Show ALL for Admin/Apps Team, 
  // Show Agent-specific for Agent Master, otherwise isolate by agentId
  const myApplicants = useMemo(() => {
    if (!user?.uid) return [];
    if (hasGlobalView) return applicants; 
    if (isAgentMaster) {
      return applicants.filter(a => a.agentName === currentUserData?.agentAssociation);
    }
    return applicants.filter(applicant => applicant.agentId === user.uid);
  }, [applicants, user?.uid, hasGlobalView, isAgentMaster, currentUserData]);

  // 3. Filter application records: Logic matches applicant filtering
  const myApplicationRecords = useMemo(() => {
    if (!user?.uid) return [];
    if (hasGlobalView) return applicationRecords;
    if (isAgentMaster) {
      return applicationRecords.filter(r => r.partnerName === currentUserData?.agentAssociation);
    }
    return applicationRecords.filter(record => record.agentId === user.uid);
  }, [applicationRecords, user?.uid, hasGlobalView, isAgentMaster, currentUserData]);

  return (
    <div className="animate-in fade-in duration-500">
      {/* Navigation Row */}
      <div className="relative flex items-center justify-center mb-10 min-h-[48px]">
        <button 
          onClick={onBack} 
          className="absolute left-0 p-2 text-slate-400 hover:text-[#0464AD] hover:bg-slate-100 rounded-xl transition-all active:scale-90"
          title="Back to Dashboard"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="flex bg-slate-100 p-1.5 rounded-2xl shadow-inner border border-slate-200/50">
          {[
            { id: 'dashboard', label: 'Dashboard' },
            { id: 'applicants', label: 'Applicants' },
            { id: 'applications', label: 'Applications' }
          ].map(tab => (
            <button 
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`px-8 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                activeTab === tab.id 
                ? 'bg-white text-[#0464AD] shadow-sm' 
                : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Internal Content Area - Passing viewOnly flag to children */}
      <div className="min-h-[500px]">
        {activeTab === 'applicants' && (
          <Applicants 
            user={user}
            settings={settings} 
            applicants={myApplicants}
            applications={applications} 
            onSaveApplicant={onSaveApplicant}
            onDeleteApplicant={onDeleteApplicant}
            viewOnly={isAgentMaster}
          />
        )}
        
        {activeTab === 'applications' && (
          <Applications 
            user={user}
            applicants={myApplicants} 
            settings={settings} 
            applications={myApplicationRecords}
            onSaveApplication={onSaveApplication}
            viewOnly={isAgentMaster}
          />
        )}
        
        {activeTab === 'dashboard' && (
          <SubmitDashboard 
            applicationRecords={myApplicationRecords} 
          />
        )}
      </div>
    </div>
  );
};

export default SubmitApplication;